import { getString } from '@/playback/i18n/bundle.generated'
import type { OpenModalHandle } from '@/components/modalLayer'
import { ctx } from '@/util/context'

const { errorHandler } = ctx('playbackConcurrencyModal')

/**
 * Multi-device playback conflict modal. Pop this when the gateway
 * surfaces a MAX_CONCURRENCY_REACHED error during playback start.
 * Mirrors GH's PlaybackConcurrencyModal — informs the user the
 * stream was paused on another device and offers to resume here.
 *
 * The modal chrome (`systemModal`) is imported lazily so it splits
 * into its own chunk rather than riding the playback-binding chunk;
 * this is a rare error path, so the load latency is unobservable.
 *
 * Returns a `dismiss` that drops the prompt — the caller uses it to
 * supersede a stale prompt when a newer entry starts (GH's
 * `dismissPlaybackConcurrencyModal`). It is safe to call before the lazy
 * chunk resolves: the open is then skipped.
 */
export function openPlaybackConcurrencyModal(options: { onResume?: () => void }): () => void {
    let handle: OpenModalHandle | null = null
    let dismissed = false
    import('@/components/systemModal')
        .then((m) => {
            if (dismissed) return
            handle = m.openSystemModal({
                className: 'playbackConcurrencyModal',
                title: getString('dmusic_playback_concurrency_modal_title'),
                body: getString('dmusic_playback_concurrency_modal_limit_reached'),
                primary: {
                    label: getString('dmusic_playback_concurrency_modal_continue'),
                    run: () => options.onResume?.(),
                },
                secondary: {
                    label: getString('dmusic_playback_concurrency_modal_dismiss'),
                    run: () => undefined,
                },
            })
        })
        .catch(errorHandler)
    return () => {
        dismissed = true
        handle?.close()
    }
}
